import type { FieldTree } from '@angular/forms/signals';
import { readonly } from '@angular/forms/signals';

import type { BackgroundAtDevice, BackgroundFile, BackgroundSettings, Device } from '@sites/entity-core';
import { BACKGROUND_LAYER_SEEDS, DEFAULT_BACKGROUND_SCROLL_EFFECT } from '@sites/entity-core';
import { ensureChild, writeDevice } from '../../core/write-device';

export type BackgroundColor = BackgroundSettings['color']['desktop'];

// Thunk is mandatory: the form model starts empty, so the background FieldTree
// node does not exist until the entity is synced.
export const createBackgroundAccessors = (getField: () => FieldTree<BackgroundSettings>) => {
    const accessors = {
        get background() {
            return getField();
        },

        setBackgroundColor: (value: BackgroundColor, device: Device = 'desktop') => {
            writeDevice(getField().color, device, () => value);
        },
        setBackgroundImage: (value: BackgroundFile | null, device: Device = 'desktop') => {
            writeDevice(getField().image, device, () => value);
        },
        /**
         * `background-size` / `-position` / `-repeat`, which the picker emits
         * together. An empty string is stored as `null`, the one spelling of unset.
         */
        setBackgroundLayout: (
            patch: { size?: string | null; position?: string | null; repeat?: string | null },
            device: Device = 'desktop'
        ) => {
            (['size', 'position', 'repeat'] as const).forEach((key) => {
                const value = patch[key];

                if (value !== undefined) writeDevice(getField()[key], device, () => value || null);
            });
        },
        setBackgroundOverlay: (
            patch: { isEnabled?: boolean; color?: string; opacity?: number },
            device: Device = 'desktop'
        ) => {
            const overlay = getField().overlay;
            const seeds = BACKGROUND_LAYER_SEEDS.overlay;
            // A layer that was off keeps only its switch; switching it on, or writing
            // one of its values, seeds the rest.
            const color = () => ensureChild(overlay, 'color', () => ({ desktop: seeds.color }));
            const opacity = () => ensureChild(overlay, 'opacity', () => ({ desktop: seeds.opacity }));

            if (patch.isEnabled !== undefined) writeDevice(overlay.isEnabled, device, () => patch.isEnabled);
            if (patch.isEnabled) {
                color();
                opacity();
            }
            if (patch.color !== undefined) writeDevice(color(), device, () => patch.color);
            if (patch.opacity !== undefined) writeDevice(opacity(), device, () => patch.opacity);
        },
        setBackgroundFilter: (patch: { isEnabled?: boolean; value?: string }, device: Device = 'desktop') => {
            const filter = getField().filter;
            const value = () => ensureChild(filter, 'value', () => ({ desktop: BACKGROUND_LAYER_SEEDS.filter.value }));

            if (patch.isEnabled !== undefined) writeDevice(filter.isEnabled, device, () => patch.isEnabled);
            if (patch.isEnabled) value();
            if (patch.value !== undefined) writeDevice(value(), device, () => patch.value);
        },

        /**
         * A {@link BackgroundAtDevice} back onto one breakpoint; a key left out is not written. Its scroll
         * effect is not either — on a block that is `setBackgroundScrollEffect`, a column has its own feature.
         */
        setBackgroundAtDevice: (value: Partial<BackgroundAtDevice>, device: Device = 'desktop') => {
            if (value.image !== undefined) accessors.setBackgroundImage(value.image, device);
            accessors.setBackgroundLayout({ size: value.size, position: value.position, repeat: value.repeat }, device);
            if (value.overlay) accessors.setBackgroundOverlay(value.overlay, device);
            if (value.filter) accessors.setBackgroundFilter(value.filter, device);
        },
    };

    return accessors;
};

/**
 * The block's two extras on top of the shared layer. Optional in the schema, so
 * a write to a background that has none seeds the key first rather than reaching
 * for a node that does not exist.
 */
export const createBackgroundBlockLayerAccessors = (getField: () => FieldTree<BackgroundSettings>) => {
    const ensure = <K extends 'video' | 'scrollEffect'>(key: K, seed: () => NonNullable<BackgroundSettings[K]>) => {
        if (!(getField()().value() as BackgroundSettings)[key]) {
            getField()().value.update((current: BackgroundSettings) => ({ ...current, [key]: seed() }));
        }

        return (getField() as any)[key];
    };

    return {
        setBackgroundVideo: (value: BackgroundFile | null, device: Device = 'desktop') => {
            writeDevice(
                ensure('video', () => ({ desktop: null })),
                device,
                () => value
            );
        },
        setBackgroundScrollEffect: (patch: { isEnabled?: boolean; value?: string }, device: Device = 'desktop') => {
            const scrollEffect = ensure('scrollEffect', () => ({ isEnabled: { desktop: false } }));
            const value = () =>
                ensureChild(scrollEffect, 'value', () => ({ desktop: DEFAULT_BACKGROUND_SCROLL_EFFECT }));

            if (patch.isEnabled !== undefined) writeDevice(scrollEffect.isEnabled, device, () => patch.isEnabled);
            if (patch.isEnabled) value();
            if (patch.value !== undefined) writeDevice(value(), device, () => patch.value);
        },
    };
};

export type BackgroundAccessors = ReturnType<typeof createBackgroundAccessors>;
export type BackgroundBlockLayerAccessors = ReturnType<typeof createBackgroundBlockLayerAccessors>;

// Schema-path rules: keep the nested feature version read-only, like
// schemaPath.version at entity level.
export const applyBackgroundFieldRules = (backgroundPath: any): void => {
    readonly(backgroundPath.version);
};
